/**
 * PKCE (RFC 7636) + state handling for Google / Microsoft sign-in.
 *
 * The browser generates a random code verifier, sends only its SHA-256
 * challenge to the API, and keeps the verifier — together with the signed
 * `state` the API returns — in sessionStorage until Google redirects back.
 */

const STORAGE_KEY_PREFIX = "compbox-oauth-";

function base64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** A 64-char verifier and its S256 challenge. */
export async function createPkcePair(): Promise<{ verifier: string; challenge: string }> {
  const random = new Uint8Array(48);
  crypto.getRandomValues(random);
  const verifier = base64Url(random);
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return { verifier, challenge: base64Url(new Uint8Array(digest)) };
}

export interface PendingSignIn {
  state: string;
  verifier: string;
}

export function savePendingSignIn(provider: string, pending: PendingSignIn): void {
  sessionStorage.setItem(STORAGE_KEY_PREFIX + provider, JSON.stringify(pending));
}

/** Read and clear the pending sign-in for `provider` (one use). */
export function takePendingSignIn(provider: string): PendingSignIn | null {
  try {
    const key = STORAGE_KEY_PREFIX + provider;
    const raw = sessionStorage.getItem(key);
    sessionStorage.removeItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PendingSignIn>;
    return typeof parsed.state === "string" && typeof parsed.verifier === "string"
      ? { state: parsed.state, verifier: parsed.verifier }
      : null;
  } catch {
    return null;
  }
}
